import type { ReactNode } from 'react';
import { memo, useCallback, useEffect, useState } from 'react';
import { Modal, Text, Button, Box, TextInput } from '@mantine/core';
import { FormFooter } from '@/components/common/form';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconCheck,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  Z_INDEX_MODAL_CRITICAL,
  Z_INDEX_MODAL_CRITICAL_BACKDROP,
} from '@/constants/zIndex';
import type {
  ConfirmationModalProps,
  ConfirmationModalVariant,
} from './ConfirmationModal.types';
import styles from './ConfirmationModal.module.css';

/**
 * Icon size constant for modal header icons
 */
const ICON_SIZE = 20;

/**
 * Get the appropriate button color based on variant
 */
const getButtonColor = (variant: ConfirmationModalVariant): string => {
  switch (variant) {
    case 'danger':
      return 'red';
    case 'warning':
      return 'orange';
    default:
      return 'indigo';
  }
};

/**
 * Get the appropriate icon class based on variant
 */
const getIconClass = (
  variant: ConfirmationModalVariant
): string | undefined => {
  switch (variant) {
    case 'danger':
      return styles.iconDanger;
    case 'warning':
      return styles.iconWarning;
    default:
      return styles.iconDefault;
  }
};

/**
 * Get the default icon based on variant
 */
const getDefaultIcon = (variant: ConfirmationModalVariant): ReactNode => {
  const iconClass = getIconClass(variant);

  if (variant === 'danger' || variant === 'warning') {
    return <IconAlertTriangle size={ICON_SIZE} className={iconClass} />;
  }

  return <IconAlertCircle size={ICON_SIZE} className={iconClass} />;
};

/**
 * ConfirmationModal Component
 *
 * A generic confirmation modal that replaces both UnsavedChangesDialog
 * and ConfirmNavigationDialog. Supports multiple variants for different
 * confirmation contexts (default, danger, warning).
 *
 * Features:
 * - Async onConfirm support with internal loading state management
 * - Variant-based styling (default=blue, danger=red, warning=orange)
 * - Default icons per variant
 * - i18n integration for default button labels
 * - Critical z-index level for visibility above other modals
 *
 * @example
 * ```tsx
 * <ConfirmationModal
 *   opened={isOpen}
 *   onClose={handleClose}
 *   onConfirm={handleDelete}
 *   title="Delete Task?"
 *   message="This action cannot be undone."
 *   variant="danger"
 * />
 * ```
 */
export const ConfirmationModal = memo<ConfirmationModalProps>(
  function ConfirmationModal({
    opened,
    onClose,
    onConfirm,
    title,
    message,
    icon,
    confirmLabel,
    cancelLabel,
    variant = 'default',
    isLoading: externalLoading,
    requireConfirmText,
    confirmTextLabel,
  }) {
    const { t } = useTranslation();
    const [internalLoading, setInternalLoading] = useState(false);
    const [confirmTextValue, setConfirmTextValue] = useState('');

    // Use external loading state if provided, otherwise manage internally
    const isLoading = externalLoading ?? internalLoading;

    // Check if text confirmation matches
    const textConfirmationRequired = requireConfirmText !== undefined;
    const textConfirmationMatches = confirmTextValue === requireConfirmText;
    const isConfirmDisabled =
      textConfirmationRequired && !textConfirmationMatches;

    // Clear confirmation text when modal closes
    useEffect(() => {
      if (!opened) {
        setConfirmTextValue('');
      }
    }, [opened]);

    const handleConfirm = useCallback(async () => {
      const result = onConfirm();

      // If onConfirm returns a promise, manage loading state internally
      if (result instanceof Promise) {
        setInternalLoading(true);
        try {
          await result;
        } finally {
          setInternalLoading(false);
        }
      }
    }, [onConfirm]);

    const displayIcon = icon ?? getDefaultIcon(variant);
    const buttonColor = getButtonColor(variant);

    return (
      <Modal
        opened={opened}
        onClose={onClose}
        title={
          <Box className={styles.header}>
            {displayIcon}
            <span>{title}</span>
          </Box>
        }
        centered
        size="md"
        closeOnClickOutside={!isLoading}
        closeOnEscape={!isLoading}
        trapFocus
        withCloseButton={!isLoading}
        closeButtonProps={{ 'aria-label': t('accessibility.closeModal') }}
        zIndex={Z_INDEX_MODAL_CRITICAL}
        styles={{
          overlay: {
            zIndex: Z_INDEX_MODAL_CRITICAL_BACKDROP,
          },
        }}
      >
        <Text size="sm" mb={textConfirmationRequired ? 'sm' : 'lg'}>
          {message}
        </Text>

        {textConfirmationRequired && (
          <Box className={styles.confirmTextContainer}>
            <TextInput
              className={styles.confirmTextInput}
              label={confirmTextLabel ?? t('common.typeToConfirm')}
              value={confirmTextValue}
              onChange={(event) =>
                setConfirmTextValue(event.currentTarget.value)
              }
              placeholder={requireConfirmText}
              disabled={isLoading}
              autoComplete="off"
              data-autofocus
            />
            <div className={styles.confirmTextHelper}>
              <span>
                {t('common.typeExactText')}{' '}
                <span className={styles.requiredText}>
                  {requireConfirmText}
                </span>
              </span>
              {textConfirmationMatches && (
                <span className={styles.matchIndicator}>
                  <IconCheck size={16} />
                </span>
              )}
            </div>
          </Box>
        )}

        <FormFooter
          leftActions={
            <Button variant="default" onClick={onClose} disabled={isLoading}>
              {cancelLabel ?? t('common.cancel')}
            </Button>
          }
          rightActions={
            <Button
              color={buttonColor}
              onClick={handleConfirm}
              loading={isLoading}
              disabled={isConfirmDisabled}
            >
              {confirmLabel ?? t('common.confirm')}
            </Button>
          }
        />
      </Modal>
    );
  }
);

ConfirmationModal.displayName = 'ConfirmationModal';
